import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { Period } from "./common"
import { PersonalNotificationIcon } from "./personal-notification-icon"
import { WelcomeDeliveryMasterId, ItemIdSchema, BulkDeliveryMasterIdSchema } from "../id"
import { Scope } from "./coin"
import { DocumentSnapshotLike } from "../firestore"
import { LocalizedLabels } from "./bulk-delivery-master"

const WelcomeDeliveryContentCoin = z.object({
  kind: z.literal("coin"),
  coin: z.object({
    scope: z.nativeEnum(Scope).describe("コインスコープ"),
    amount: z.number().describe("金額"),
    expiry: z.number().nullable().optional().describe("コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
  }),
})

const WelcomeDeliveryContentItem = z.object({
  kind: z.literal("item"),
  item: z.object({
    item_ids: z.array(ItemIdSchema).min(1).describe("配布するアイテムIDのリスト"),
  }),
})

const WelcomeDeliveryContent = z.discriminatedUnion("kind", [
  WelcomeDeliveryContentCoin,
  WelcomeDeliveryContentItem,
])

export type WelcomeDeliveryContent = z.infer<typeof WelcomeDeliveryContent>

export const WelcomeDeliveryMasterMeta = z.object({
  name: z.string().min(1).max(50).describe("補填名"),
  labels: LocalizedLabels.optional(),
  target_user_created_period: Period.describe("対象ユーザー作成期間（この期間に登録したユーザーが配信対象）"),
  origin_bulk_delivery_id: BulkDeliveryMasterIdSchema.optional().describe("元となったBulkDeliveryMasterのID（自動作成された場合）"),
  payload: z.object({
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
    delivery_content: WelcomeDeliveryContent.describe("配信内容（コインまたはアイテム）"),
    expire_at: z.number().describe("受け取り期限（UNIX Timestamp mills、絶対時刻）"),
  }),
})

export type WelcomeDeliveryMasterMeta = z.infer<typeof WelcomeDeliveryMasterMeta>

export const WelcomeDeliveryMasterDocumentBody = WelcomeDeliveryMasterMeta.extend({
  reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type WelcomeDeliveryMasterDocumentBody = z.infer<typeof WelcomeDeliveryMasterDocumentBody>

export type WelcomeDeliveryMaster = WelcomeDeliveryMasterDocumentBody & {
  id: WelcomeDeliveryMasterId
}

// API リクエスト用のZodスキーマ（camelCase）
// OpenAPI の WelcomeDeliveryMasterCommon に対応

const WelcomeDeliveryContentCoinRequest = z.object({
  kind: z.literal("coin"),
  coin: z.object({
    scope: z.nativeEnum(Scope).describe("コインスコープ"),
    amount: z.number().describe("金額"),
    expiry: z.number().nullable().optional().describe("コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
  }),
})

const WelcomeDeliveryContentItemRequest = z.object({
  kind: z.literal("item"),
  item: z.object({
    itemIds: z.array(ItemIdSchema).min(1).describe("配布するアイテムIDのリスト"),
  }),
})

const WelcomeDeliveryContentRequest = z.discriminatedUnion("kind", [
  WelcomeDeliveryContentCoinRequest,
  WelcomeDeliveryContentItemRequest,
])

export const CreateWelcomeDeliveryMasterRequest = z.object({
  name: z.string().min(1).max(50).describe("配信名"),
  labels: z.object({
    name: z.record(z.string(), z.string()).optional().describe("多言語対応の配信名"),
  }).optional().describe("多言語対応のラベル"),
  targetUserCreatedPeriod: z.object({
    startAt: z.number().describe("対象開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("対象終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).describe("対象ユーザー作成期間"),
  payload: z.object({
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
    deliveryContent: WelcomeDeliveryContentRequest.describe("配信内容（コインまたはアイテム）"),
    expireAt: z.number().describe("受け取り期限（UNIX Timestamp mills、絶対時刻）"),
  }).describe("配信ペイロード"),
})

export type CreateWelcomeDeliveryMasterRequest = z.infer<typeof CreateWelcomeDeliveryMasterRequest>

export const UpdateWelcomeDeliveryMasterRequest = z.object({
  name: z.string().min(1).max(50).optional().describe("配信名"),
  labels: z.object({
    name: z.record(z.string(), z.string()).optional().describe("多言語対応の配信名"),
  }).optional().describe("多言語対応のラベル"),
  targetUserCreatedPeriod: z.object({
    startAt: z.number().describe("対象開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("対象終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).optional().describe("対象ユーザー作成期間"),
  payload: z.object({
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
    deliveryContent: WelcomeDeliveryContentRequest.describe("配信内容（コインまたはアイテム）"),
    expireAt: z.number().describe("受け取り期限（UNIX Timestamp mills、絶対時刻）"),
  }).optional().describe("配信ペイロード"),
})

export type UpdateWelcomeDeliveryMasterRequest = z.infer<typeof UpdateWelcomeDeliveryMasterRequest>

export class WelcomeDeliveryMasterDocuments {
  static toDocumentData(master: WelcomeDeliveryMaster): object {
    return {
      ...master,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): WelcomeDeliveryMaster | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const payload = data["payload"]

    // 旧形式（item.item_id）から新形式（item.item_ids）への変換（後方互換性）
    if (payload?.delivery_content?.kind === "item" && payload.delivery_content.item) {
      const item = payload.delivery_content.item
      if ("item_id" in item && !("item_ids" in item)) {
        // 旧形式を検出: item_idを配列に変換
        payload.delivery_content.item = {
          item_ids: [item.item_id]
        }
      }
    }

    return {
      id: WelcomeDeliveryMasterId(doc.id),
      name: data["name"],
      labels: data["labels"],
      target_user_created_period: data["target_user_created_period"],
      origin_bulk_delivery_id: data["origin_bulk_delivery_id"],
      payload,
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
